import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Terms and Conditions"
        description="Terms and Conditions for T&M Construction services. Read our terms of service, payment terms, and warranty information."
        canonical="/terms"
      />
      
      <section className="pt-56 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Terms and Conditions</h1>
            <p className="text-gray-600">Last updated: January 2025</p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              These Terms and Conditions govern your use of services provided by T&M Builders Suffolk LTD 
              (trading as "T&M Construction"), registered in England and Wales with company number 14672875. 
              Our registered address is in Ipswich, UK.
            </p>
            <p className="text-gray-700 mb-4">
              By engaging our services, you agree to be bound by these terms. Please read them carefully 
              before proceeding with any work.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Services</h2>
            <p className="text-gray-700 mb-4">
              T&M Construction provides construction, renovation, painting, decorating, and maintenance 
              services across Suffolk, Essex, and London. Our services include but are not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Kitchen and bathroom renovations</li>
              <li>House extensions and loft conversions</li>
              <li>Interior and exterior painting and decorating</li>
              <li>Property refurbishment</li>
              <li>General building and maintenance work</li>
              <li>Landscaping and garden services</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Quotations and Pricing</h2>
            <p className="text-gray-700 mb-4">
              All quotations provided are valid for 30 days from the date of issue unless otherwise stated. 
              Quotations are based on the information provided at the time of survey and may be subject to 
              change if additional work is required or unforeseen circumstances arise.
            </p>
            <p className="text-gray-700 mb-4">
              Any additional work requested after the initial quotation will be subject to additional charges, 
              which will be agreed upon before the work commences.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Payment Terms</h2>
            <p className="text-gray-700 mb-4">
              Payment terms will be agreed upon before work commences. Typically, our payment structure is:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>A deposit may be required for larger projects to secure materials</li>
              <li>Stage payments may be agreed for projects exceeding one week</li>
              <li>Final payment is due upon completion of work unless otherwise agreed</li>
            </ul>
            <p className="text-gray-700 mb-4">
              We accept payment via bank transfer, cash, or cheque. Late payments may incur interest 
              charges in accordance with the Late Payment of Commercial Debts (Interest) Act 1998.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Warranty and Guarantees</h2>
            <p className="text-gray-700 mb-4">
              T&M Construction provides a workmanship warranty on all completed projects. The warranty 
              period varies depending on the type of work:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Painting and decorating: 12 months</li>
              <li>General building work: 24 months</li>
              <li>Structural work: As per building regulations</li>
            </ul>
            <p className="text-gray-700 mb-4">
              This warranty covers defects in workmanship only and does not cover normal wear and tear, 
              damage caused by third parties, or issues arising from improper use or maintenance.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Cancellation Policy</h2>
            <p className="text-gray-700 mb-4">
              You may cancel your project at any time. However:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Cancellations made within 48 hours of scheduled start date may forfeit any deposit paid</li>
              <li>If materials have been purchased specifically for your project, you may be liable for these costs</li>
              <li>Work completed up to the point of cancellation will be invoiced</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Health and Safety</h2>
            <p className="text-gray-700 mb-4">
              T&M Construction is committed to maintaining the highest standards of health and safety. 
              We hold appropriate insurance including public liability and employer's liability insurance. 
              All work is carried out in compliance with current health and safety regulations.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Insurance</h2>
            <p className="text-gray-700 mb-4">
              We maintain comprehensive insurance coverage including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Public Liability Insurance</li>
              <li>Employer's Liability Insurance</li>
              <li>Professional Indemnity Insurance</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Copies of our insurance certificates are available upon request.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Disputes</h2>
            <p className="text-gray-700 mb-4">
              In the event of any dispute, we encourage you to contact us directly to resolve the matter. 
              If a resolution cannot be reached, we are members of relevant trade bodies and disputes 
              can be referred to appropriate mediation services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              T&M Construction's liability is limited to the value of the contract. We shall not be 
              liable for any indirect, consequential, or special damages arising from our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to update these terms and conditions at any time. Any changes will 
              be posted on our website and will apply to new contracts from the date of posting.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about these Terms and Conditions, please contact us:
            </p>
            <ul className="list-none text-gray-700 mb-4">
              <li><strong>Company:</strong> T&M Builders Suffolk LTD</li>
              <li><strong>Trading as:</strong> T&M Construction</li>
              <li><strong>Company Number:</strong> 14672875</li>
              <li><strong>VAT Number:</strong> 495724938</li>
              <li><strong>Email:</strong> info@tandmbuilders.co.uk</li>
              <li><strong>Phone:</strong> +44 0333 335 5532</li>
              <li><strong>Location:</strong> Ipswich, UK</li>
            </ul>

            <div className="mt-12 p-6 bg-gray-50 rounded-xl">
              <p className="text-gray-600 text-sm">
                These terms and conditions are governed by the laws of England and Wales. 
                By using our services, you consent to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </div>

            <div className="mt-8 flex space-x-4">
              <Link to="/privacy" className="text-orange-600 hover:text-orange-700 font-medium">
                Privacy Policy →
              </Link>
              <Link to="/contact" className="text-orange-600 hover:text-orange-700 font-medium">
                Contact Us →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
